import { Snackbar } from "@mui/material";
import Slide, { type SlideProps } from "@mui/material/Slide";
import { styled } from "@mui/material/styles";
import {
	createContext,
	type FC,
	type PropsWithChildren,
	type ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

function SlideTransition(props: PropsWithChildren<SlideProps>) {
	return (
		<Slide {...props} direction="down">
			{props.children}
		</Slide>
	);
}

export type SnackbarContextType = {
	openSnackbarWithContent: (content: ReactElement) => void;
};

const StyledSnackbar = styled(Snackbar)(({ theme }) => ({
	padding: "20px 40px",
	width: "95%",
	margin: "auto",
	backgroundColor: theme.palette.common.white,
	borderRadius: theme.shape.borderRadius,
}));

export const SnackbarContext = createContext<SnackbarContextType>({} as SnackbarContextType);

type QueuedSnackbar = {
	id: number;
	content: ReactElement;
};

const AUTO_HIDE_DURATION_MS = 5000;

export const SnackbarContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const [open, setOpen] = useState(false);
	const [snackbarQueue, setSnackbarQueue] = useState<readonly QueuedSnackbar[]>([]);
	const [currentSnackbar, setCurrentSnackbar] = useState<QueuedSnackbar | undefined>(undefined);
	const snackbarIdRef = useRef(0);

	useEffect(() => {
		if (snackbarQueue.length > 0 && !currentSnackbar) {
			// Set a new snack when we don't have an active one
			setCurrentSnackbar(snackbarQueue[0]);
			setSnackbarQueue((prev) => prev.slice(1));
			setOpen(true);
		} else if (snackbarQueue.length > 0 && currentSnackbar && open) {
			// Close an active snack when a new one is added
			setOpen(false);
		}
	}, [snackbarQueue, currentSnackbar, open]);

	// Memoised so consumers (e.g. useOrderSteps) that list this in effect deps don't
	// re-fire on every queue advance — that loop was enqueuing snackbars indefinitely
	// while the StockCheck branch matched. setSnackbarQueue is stable; snackbarIdRef
	// is a ref. No deps needed.
	const openSnackbarWithContent = useCallback((content: ReactElement) => {
		const id = snackbarIdRef.current;
		snackbarIdRef.current += 1;
		setSnackbarQueue((prev) => [...prev, { id: id, content: content }]);
	}, []);

	const handleClose = useCallback(() => {
		setOpen(false);
	}, []);

	const handleExited = useCallback(() => {
		setCurrentSnackbar(undefined);
	}, []);

	// Own auto-hide timer that cannot be paused by MUI's hover/focus handlers.
	// MUI's built-in autoHideDuration pauses on mouseenter and never resumes
	// on kiosk touchscreens (mouseleave doesn't fire after touch).
	useEffect(() => {
		if (!open) {
			return;
		}
		const timer = setTimeout(handleClose, AUTO_HIDE_DURATION_MS);
		return () => {
			clearTimeout(timer);
		};
	}, [open, handleClose]);

	const contextValue = useMemo(() => ({ openSnackbarWithContent: openSnackbarWithContent }), [openSnackbarWithContent]);

	return (
		<SnackbarContext.Provider value={contextValue}>
			<StyledSnackbar
				key={currentSnackbar?.id}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				disableWindowBlurListener={true}
				// `open` and `currentSnackbar` are advanced from two separate effects; during
				// rapid back-to-back queues (e.g., login → re-login firing welcome twice) the
				// transition can leave `open=true` momentarily while `currentSnackbar` is
				// undefined. Without this guard the styled wrapper renders as a blank white
				// box at the top of the viewport.
				open={open && currentSnackbar != null}
				slots={{
					transition: SlideTransition,
				}}
				slotProps={{
					transition: { onExited: handleExited },
				}}
			>
				{currentSnackbar?.content ?? <span />}
			</StyledSnackbar>
			{children}
		</SnackbarContext.Provider>
	);
};

export const useSnackbarContext = () => useContext(SnackbarContext);
